// How much is actually in a block's Custom CSS. The field is folded away by
// default, so the control that holds it has to be able to say whether folding
// it away is hiding anything — and "3 declarations" says that where "47 lines"
// would count a comment, a blank line and a closing brace as content.
//
// A declaration is a `property: value` pair inside a rule: the thing that
// actually changes how the block looks. Rules and at-rules are not counted —
// one `@media` wrapping one selector is a shape almost every one of these
// fields has (it is what the on-site editor writes), and counting it would
// report 2 for a block whose author has set nothing.
//
// Hand-scanned rather than matched with a regex, because the three things that
// would make a regex wrong are all ordinary here: a `@media (max-width: 767px)`
// prelude carries a colon that declares nothing, a `url(data:image/png;base64,…)`
// carries both a colon and a semicolon inside one value, and a comment can
// carry anything at all.
const isDeclaration = (statement, depth) => depth > 0 && statement.includes(':') && statement.trim() !== '';

export const countCssDeclarations = (css) => {
  if (typeof css !== 'string' || css === '') {
    return 0;
  }

  let count = 0;
  // Depth of `{}` nesting: a declaration only exists inside a rule, so this is
  // what tells `color: red` apart from the `(max-width: 767px)` above it.
  let depth = 0;
  let parens = 0;
  let quote = null;
  let statement = '';

  for (let i = 0; i < css.length; i += 1) {
    const character = css[i];

    if (quote) {
      // A backslash escapes whatever follows it, the closing quote included.
      if (character === '\\') {
        i += 1;
      } else if (character === quote) {
        quote = null;
      }
      continue;
    }

    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }

    if (character === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2);
      // An unterminated comment swallows the rest of the stylesheet, which is
      // exactly what a browser does with one.
      i = end === -1 ? css.length : end + 1;
      continue;
    }

    // Everything between parentheses belongs to the value it sits in — it has
    // already been counted by the `:` that opened the declaration, and its own
    // punctuation must not open or close another.
    if (parens > 0) {
      if (character === '(') {
        parens += 1;
      } else if (character === ')') {
        parens -= 1;
      }
      continue;
    }

    if (character === '(') {
      parens = 1;
      continue;
    }

    if (character === '{') {
      depth += 1;
      statement = '';
      continue;
    }

    if (character === '}' || character === ';') {
      if (isDeclaration(statement, depth)) {
        count += 1;
      }
      if (character === '}') {
        depth = Math.max(0, depth - 1);
      }
      statement = '';
      continue;
    }

    statement += character;
  }

  return count;
};
